/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import {
    Configuration,
    Credit,
    CreditType,
    Person,
    PersonContentApi,
} from 'client-code/person-content-service';

import { CommonHeaders } from '../CommonHeaders';
import { IPersonContentServiceRepository } from '../interfaces';
import { TServicesParams } from '../types';
import { UniqueIdentifier } from '../utils';

/**
 * @inheritDoc
 */
export class PersonContentServiceRepositoryImpl implements IPersonContentServiceRepository {
    private static readonly TAG = 'PersonContentServiceRepositoryImpl';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    // eslint-disable-next-line class-methods-use-this
    private buildCommonHeaders(featureArea: string): CommonHeaders {
        return new CommonHeaders(featureArea);
    }

    private buildConfiguration(headers: Record<string, string>): Configuration {
        return new Configuration({
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: 30000,
                headers,
            },
        });
    }

    private getApi(featureArea: string): PersonContentApi {
        const commonHeaders = this.buildCommonHeaders(featureArea);
        const config = this.buildConfiguration(commonHeaders.headers);
        return new PersonContentApi(config);
    }

    private getBaseRequestParams(featureArea: string): {
        applicationFeatureArea: string;
        applicationName: string;
        applicationVersion: string;
        bodyId: string;
        deviceType: string;
        productName: string;
        whiny: string;
        xAmznRequestId: string;
    } {
        const commonHeaders = this.buildCommonHeaders(featureArea).headers;
        return {
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationName: commonHeaders.ApplicationName,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            whiny: this.mParams.whiny || 'false',
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
        };
    }

    public async collectionCreditsGet({
        applicationFeatureArea,
        collectionId,
        type,
    }: {
        applicationFeatureArea: string;
        collectionId: string;
        type?: CreditType;
    }): Promise<Credit[]> {
        const api = this.getApi(applicationFeatureArea);
        const params = {
            ...this.getBaseRequestParams(applicationFeatureArea),
            collectionId,
            type,
        };
        const response = await api.collectionCreditsGet(params);
        return response.data;
    }

    public async contentCreditsGet({
        applicationFeatureArea,
        contentId,
        type,
    }: {
        applicationFeatureArea: string;
        contentId: string;
        type?: CreditType;
    }): Promise<Credit[]> {
        const api = this.getApi(applicationFeatureArea);
        const params = {
            ...this.getBaseRequestParams(applicationFeatureArea),
            contentId,
            type,
        };
        const response = await api.contentCreditsGet(params);
        return response.data;
    }

    public async personGet({
        applicationFeatureArea,
        personId,
        collectionId,
        contentId,
        includeContent,
    }: {
        applicationFeatureArea: string;
        personId: string;
        collectionId?: string;
        contentId?: string;
        includeContent?: boolean;
    }): Promise<Person> {
        const api = this.getApi(applicationFeatureArea);
        const commonHeaders = this.buildCommonHeaders(applicationFeatureArea).headers;
        const params = {
            ...this.getBaseRequestParams(applicationFeatureArea),
            acceptLanguage: commonHeaders['Accept-Language'],
            personId,
            collectionId,
            contentId,
            includeContent,
        };
        const response = await api.personGet(params);
        return response.data;
    }
}
